/**
 * ImageOrCodeSection.js
 * Section Image Docker + IDE optionnel avec régénération LOCK zones.
 */
import React from "react";
import {Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import CodeEditorIDE from "./CodeEditorIDE";
import ApiAnalyticsEngineCtrl from "../../../../../api/ApiAnalyticsEngineCtrl";
import {useTranslation} from "react-i18next";
import BuildIcon from "@mui/icons-material/Build";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import BlockIcon from "@mui/icons-material/Block";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import JSZip from "jszip";
import {saveAs} from "file-saver";

const LANGUAGES = ["PYTHON", "R"];

const DEFAULT_REQUIREMENTS = "numpy\nscipy\npandas\nscikit-learn\n";

/** Génère la valeur par défaut Python pour un param, avec cast selon le type */
function pyParamLine(p) {
    const def = p.default != null ? p.default : p.min != null ? p.min : null;
    const pyDefault = def === null ? "None" : JSON.stringify(def);
    const raw = `params.get("${p.name}", ${pyDefault})`;
    const type = (p.type || "").toUpperCase();
    if (type === "INTEGER") return `    ${p.name} = int(${raw})`;
    if (type === "FLOAT" || type === "DOUBLE" || type === "NUMBER") return `    ${p.name} = float(${raw})`;
    if (type === "BOOLEAN") return `    ${p.name} = bool(${raw})`;
    if (type === "STRING") return `    ${p.name} = str(${raw})`;
    return `    ${p.name} = ${raw}`;
}

/** Génère la valeur par défaut R pour un param, avec cast selon le type */
function rParamLine(p) {
    const def = p.default != null ? p.default : p.min != null ? p.min : null;
    const rDefault = def === null ? "NULL" : JSON.stringify(def);
    const raw = `if (!is.null(params[["${p.name}"]])) params[["${p.name}"]] else ${rDefault}`;
    const type = (p.type || "").toUpperCase();
    if (type === "INTEGER") return `    ${p.name} <- as.integer(${raw})`;
    if (type === "FLOAT" || type === "DOUBLE" || type === "NUMBER") return `    ${p.name} <- as.numeric(${raw})`;
    if (type === "BOOLEAN") return `    ${p.name} <- as.logical(${raw})`;
    if (type === "STRING") return `    ${p.name} <- as.character(${raw})`;
    return `    ${p.name} <- ${raw}`;
}

const PYTHON_HANDLER_TEMPLATE = (series, outputs, params) => {
    const args = (series || []).map(s => s.name || "observed").join(", ");
    const ret = (outputs || []).map(o => `"${o.name || "value"}": ${o.name || "value"}`).join(", ");
    const paramDefaults = (params || []).map(p => pyParamLine(p)).join("\n");
    const inits = (outputs || []).map(o => `    ${o.name || "value"} = None`).join("\n");
    return `# --- LOCK:IMPORTS:START ---
import numpy as np
import warnings
warnings.filterwarnings("ignore")
# --- LOCK:IMPORTS:END ---

# --- LOCK:PARAMS:START ---
def handle(${args}, params=None):
    if params is None:
        params = {}
${paramDefaults || "    pass"}
${inits}
# --- LOCK:PARAMS:END ---

    # ── Votre code ici ──


# --- LOCK:RETURN:START ---
    return {${ret}}
# --- LOCK:RETURN:END ---
`;
};

const R_HANDLER_TEMPLATE = (series, outputs, params) => {
    const args = (series || []).map(s => s.name || "observed").join(", ");
    const ret = (outputs || []).map(o => `${o.name || "value"} = ${o.name || "value"}`).join(", ");
    const paramDefaults = (params || []).map(p => rParamLine(p)).join("\n");
    const inits = (outputs || []).map(o => `    ${o.name || "value"} <- NULL`).join("\n");
    return `# --- LOCK:IMPORTS:START ---
library(jsonlite)
# --- LOCK:IMPORTS:END ---

# --- LOCK:PARAMS:START ---
handle <- function(${args}, params = NULL) {
    if (is.null(params)) params <- list()
${paramDefaults || "    # pas de parametres"}
${inits}
# --- LOCK:PARAMS:END ---

    # Votre code ici


# --- LOCK:RETURN:START ---
    list(${ret})
}
# --- LOCK:RETURN:END ---
`;
};

const DRY_RUN_PYTHON = `#!/usr/bin/env python3
"""Test local du handler sans plateforme. Charge sample_data.csv et appelle handle()."""
import csv, sys, inspect, numpy as np
from handler import handle

csv_file = sys.argv[1] if len(sys.argv) > 1 else "sample_data.csv"

columns = {}
with open(csv_file, newline="", encoding="utf-8") as f:
    reader = csv.DictReader(f)
    for col in reader.fieldnames:
        if col == "timestamp": continue
        columns[col] = []
    for row in reader:
        for col in columns:
            try: columns[col].append(float(row[col]))
            except (ValueError, KeyError): pass

sig = inspect.signature(handle)
kwargs = {}
series_values = list(columns.values())
for i, name in enumerate(sig.parameters):
    if name == "params": continue
    if name in columns:
        kwargs[name] = np.array(columns[name])
    elif i < len(series_values):
        kwargs[name] = np.array(series_values[i])

result = handle(**kwargs, params={})
print(f"Resultat: {result}")
`;

const DRY_RUN_R = `#!/usr/bin/env Rscript
# Test local du handler sans plateforme. Charge sample_data.csv et appelle handle().
source("handler.R")

csv_file <- if (length(commandArgs(TRUE)) > 0) commandArgs(TRUE)[1] else "sample_data.csv"
data <- read.csv(csv_file, stringsAsFactors = FALSE)

# Retirer la colonne timestamp
data$timestamp <- NULL

# Appeler handle() avec les colonnes restantes
args <- lapply(data, as.numeric)
args$params <- list()
result <- do.call(handle, args)
cat("Resultat:", paste(capture.output(str(result)), collapse = "\\n"), "\\n")
`;

function regenerateLockedZones(content, language, series, outputs, params) {
    if (!content) return content;
    const seriesArgs = (series || []).map(s => s.name || "observed");
    const outputKeys = (outputs || []).map(o => o.name || "value");
    const isPython = language === "PYTHON";
    const paramLines = (params || []).map(p => isPython ? pyParamLine(p) : rParamLine(p));
    const outputInits = isPython
        ? outputKeys.map(k => `    ${k} = None`)
        : outputKeys.map(k => `    ${k} <- NULL`);
    const zoneContent = {};
    if (isPython) {
        zoneContent["PARAMS"] = [`def handle(${seriesArgs.join(", ")}, params=None):`, "    if params is None:", "        params = {}",
            ...(paramLines.length > 0 ? paramLines : ["    pass"]),
            ...outputInits];
        zoneContent["RETURN"] = [`    return {${outputKeys.map(k => `"${k}": ${k}`).join(", ")}}`];
    } else {
        zoneContent["PARAMS"] = [`handle <- function(${seriesArgs.join(", ")}, params = NULL) {`, "    if (is.null(params)) params <- list()",
            ...(paramLines.length > 0 ? paramLines : ["    # pas de parametres"]),
            ...outputInits];
        zoneContent["RETURN"] = [`    list(${outputKeys.map(k => `${k} = ${k}`).join(", ")})`, "}"];
    }
    const lines = content.split("\n");
    const result = [];
    let skipUntilEnd = null;
    for (const line of lines) {
        const startMatch = line.match(/LOCK:(\w+):START/);
        if (startMatch) {
            result.push(line);
            if (zoneContent[startMatch[1]]) { result.push(...zoneContent[startMatch[1]]); skipUntilEnd = startMatch[1]; }
            continue;
        }
        if (skipUntilEnd) {
            const endMatch = line.match(/LOCK:(\w+):END/);
            if (endMatch && endMatch[1] === skipUntilEnd) { result.push(line); skipUntilEnd = null; }
            continue;
        }
        result.push(line);
    }
    return result.join("\n");
}

export default function ImageOrCodeSection({form, setField, versions = [], onSwitchVersion, isEdit = false}) {
    const {t} = useTranslation();
    // sourceMode : BUILD (IDE code), PULL (image existante), NONE (rien)
    const [sourceMode, setSourceModeState] = React.useState(() => {
        const img = typeof form.image === "object" ? form.image : null;
        return img?.sourceMode || "BUILD";
    });
    const [language, setLanguageState] = React.useState("PYTHON");
    const [files, setFilesState] = React.useState({});
    // Fichiers plateforme (connector.py / connector.R) — lecture seule
    const [platformFiles, setPlatformFiles] = React.useState({});

    // Confirmation avant réinitialisation du template (écrase le code utilisateur)
    const [resetConfirmOpen, setResetConfirmOpen] = React.useState(false);
    const [copiedKey, setCopiedKey] = React.useState(null);

    // Fichiers utilisateur copiables dans le popup de réinitialisation
    const copyableFiles = React.useMemo(() => {
        const ext = language === "R" ? ".R" : ".py";
        const candidates = [`handler${ext}`, "requirements.txt", "install_packages.R"];
        return candidates.filter(name => files[name] != null && files[name] !== "");
    }, [files, language]);

    async function copyFileToClipboard(name) {
        try {
            await navigator.clipboard.writeText(files[name] ?? "");
            setCopiedKey(name);
            setTimeout(() => setCopiedKey(prev => (prev === name ? null : prev)), 2000);
        } catch (e) {
            // Clipboard indisponible (contexte non sécurisé) — fallback execCommand
            const ta = document.createElement("textarea");
            ta.value = files[name] ?? "";
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); setCopiedKey(name); setTimeout(() => setCopiedKey(prev => (prev === name ? null : prev)), 2000); } catch (_) {}
            document.body.removeChild(ta);
        }
    }

    function downloadFile(name) {
        const blob = new Blob([files[name] ?? ""], {type: "text/plain;charset=utf-8"});
        saveAs(blob, name);
    }

    function confirmResetTemplate() {
        setResetConfirmOpen(false);
        setCopiedKey(null);
        // Re-synchronise aussi les fichiers plateforme (connector) depuis le backend :
        // une réinitialisation doit ramener le DERNIER template, pas la copie chargée
        // à l'ouverture du dialog (le connector plateforme a pu changer côté backend).
        resyncPlatformFiles();
        generateFromScratch();
    }

    // Charger les fichiers plateforme (connector.py / connector.R) pour le langage courant
    React.useEffect(() => {
        let cancelled = false;
        ApiAnalyticsEngineCtrl.getPlatformFiles(language).then(({data}) => {
            if (!cancelled && data) setPlatformFiles(data);
        });
        return () => { cancelled = true; };
    }, [language]);

    // Re-récupère le connector depuis le backend (utile après un nouveau déploiement
    // du catalog-manager : le contenu du connector plateforme change sans rebuild front).
    function resyncPlatformFiles() {
        ApiAnalyticsEngineCtrl.getPlatformFiles(language)
            .then(({data}) => { if (data) setPlatformFiles({...data}); });
    }

    // Bouton refresh dans l'arbre :
    //  - fichier plateforme (connector, lecture seule) → resync depuis le backend
    //  - fichier réinitialisable (requirements) → remise au contenu par défaut du template
    function refreshFile(path, isReadOnly) {
        if (isReadOnly) {
            resyncPlatformFiles();
            return;
        }
        if (path === "requirements.txt") {
            setFilesState(prev => {
                const next = {...prev, "requirements.txt": DEFAULT_REQUIREMENTS};
                const img = parseImageRef();
                img.files = next; img.language = language; img.sourceMode = sourceMode;
                setField("image", img);
                return next;
            });
        } else if (path === "install_packages.R") {
            setFilesState(prev => {
                const next = {...prev, "install_packages.R": 'install.packages(c("jsonlite"))\n'};
                const img = parseImageRef();
                img.files = next; img.language = language; img.sourceMode = sourceMode;
                setField("image", img);
                return next;
            });
        }
    }

    // Ref pour toujours avoir la valeur courante de form.image (évite les closures stale)
    const imageRef = React.useRef(form.image);
    React.useEffect(() => { imageRef.current = form.image; }, [form.image]);

    // Synchroniser les fichiers locaux quand form.image.files change (switch version, édition, nouvelle version)
    const formFilesKey = React.useMemo(() => {
        const img = typeof form.image === "object" ? form.image : null;
        if (!img?.files) return "";
        // Inclure un hash du contenu pour détecter les changements de contenu (pas juste les clés)
        const entries = Object.entries(img.files).sort(([a], [b]) => a.localeCompare(b));
        return JSON.stringify(entries.map(([k, v]) => [k, v?.length ?? 0]));
    }, [form.image]);

    React.useEffect(() => {
        const img = typeof form.image === "object" ? form.image : null;
        if (img?.files && Object.keys(img.files).length > 0) {
            setFilesState(img.files);
            if (img.language) setLanguageState(img.language);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formFilesKey]);

    function parseImageRef() {
        const cur = imageRef.current;
        if (typeof cur === "object" && cur) return {...cur};
        if (typeof cur === "string" && cur) {
            const m = cur.match(/^(.+?)\/(.+):(.+)$/);
            return m ? {registry: m[1], image: m[2], tag: m[3]} : {registry: "", image: cur, tag: ""};
        }
        return {};
    }

    function setSourceMode(newMode) {
        setSourceModeState(newMode);
        const img = parseImageRef();
        img.sourceMode = newMode;
        if (newMode === "BUILD") {
            img.files = files; img.language = language;
        }
        setField("image", img);
    }

    function setFiles(updater) {
        setFilesState(prev => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            const img = parseImageRef();
            img.files = next; img.language = language; img.sourceMode = sourceMode;
            setField("image", img);
            return next;
        });
    }
    function setLanguage(lang) {
        setLanguageState(lang);
        if (Object.keys(files).length > 0) {
            const img = parseImageRef();
            img.language = lang; img.files = files; img.sourceMode = sourceMode; setField("image", img);
        }
    }

    const depsKey = JSON.stringify({
        series: (form.series || []).map(s => s.name), outputs: (form.outputs || []).map(o => o.name),
        params: (form.params || []).map(p => ({name: p.name, default: p.default, type: p.type, min: p.min})),
        n: (form.series || []).length + (form.outputs || []).length + (form.params || []).length,
    });
    // Régénérer les zones LOCK quand series/outputs/params changent OU au premier chargement des fichiers
    const filesLoadedRef = React.useRef(false);
    React.useEffect(() => {
        if (Object.keys(files).length === 0) return;
        const ext = language === "R" ? ".R" : ".py";
        const hf = `handler${ext}`;
        if (!files[hf]) return;
        const updated = regenerateLockedZones(files[hf], language, form.series, form.outputs, form.params);
        if (updated !== files[hf]) setFiles(prev => ({...prev, [hf]: updated}));
        filesLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depsKey, Object.keys(files).length]);

    function generateFromScratch() {
        const ext = language === "R" ? ".R" : ".py";
        const handler = language === "PYTHON"
            ? PYTHON_HANDLER_TEMPLATE(form.series, form.outputs, form.params)
            : R_HANDLER_TEMPLATE(form.series, form.outputs, form.params);
        const newFiles = {
            [`handler${ext}`]: handler,
            ...(language === "PYTHON" ? {"requirements.txt": DEFAULT_REQUIREMENTS} : {"install_packages.R": 'install.packages(c("jsonlite"))\n'}),
        };
        // Mettre à jour les fichiers locaux sans écraser registry/image/tag
        setFilesState(newFiles);
        // Enrichir l'image existante avec files + language
        const cur = imageRef.current;
        let img;
        if (typeof cur === "object" && cur) {
            img = {...cur};
        } else if (typeof cur === "string" && cur) {
            // Parser la string "registry/image:tag" en objet
            const m = cur.match(/^(.+?)\/(.+):(.+)$/);
            img = m ? {registry: m[1], image: m[2], tag: m[3]} : {registry: "", image: cur, tag: ""};
        } else {
            img = {};
        }
        img.files = newFiles;
        img.language = language;
        img.sourceMode = sourceMode;
        setField("image", img);
    }

    const imageStr = typeof form.image === "string" ? form.image
        : (form.image ? `${form.image.registry || ""}/${form.image.image || ""}:${form.image.tag || ""}` : "");

    function handleImageChange(e) {
        const raw = e.target.value;
        const m = raw.match(/^(.+?)\/(.+):(.+)$/);
        if (m) setField("image", {registry: m[1], image: m[2], tag: m[3]});
        else setField("image", raw);
    }

    const currentVersion = form.version || (typeof form.image === "object" ? form.image?.tag : "") || "";

    /** Exporte le modèle + fichiers sources en archive ZIP compatible upload */
    async function exportZip() {
        const zip = new JSZip();

        // template.json — le modèle complet sans les files (ils sont à la racine du ZIP)
        const {image, ...rest} = form;
        const img = typeof image === "object" ? image : {};
        const {files: _f, ...imgClean} = img;
        const templateJson = {...rest, image: imgClean, language};
        zip.file("template.json", JSON.stringify(templateJson, null, 2));

        // Fichiers sources (handler.py, requirements.txt, etc.)
        Object.entries(files).forEach(([name, content]) => {
            zip.file(name, content || "");
        });

        // dry_run — script de test local
        if (language === "PYTHON") {
            zip.file("dry_run.py", DRY_RUN_PYTHON);
        } else {
            zip.file("dry_run.R", DRY_RUN_R);
        }

        // sample_data.csv — données de test
        const seriesNames = (form.series || []).map(s => s.name || "observed");
        const csvHeader = ["timestamp", ...seriesNames].join(",");
        const csvRows = Array.from({length: 20}, (_, i) => {
            const ts = `2026-01-01T${String(i).padStart(2, "0")}:00:00Z`;
            const values = seriesNames.map(() => (10 + Math.random() * 2 - 1).toFixed(1));
            return [ts, ...values].join(",");
        });
        zip.file("sample_data.csv", [csvHeader, ...csvRows].join("\n") + "\n");

        const blob = await zip.generateAsync({type: "blob"});
        saveAs(blob, `${form.id || "model"}.zip`);
    }

    // Auto-charger le template à l'ouverture si pas de fichiers
    React.useEffect(() => {
        if (sourceMode === "BUILD" && Object.keys(files).length === 0) {
            const img = typeof form.image === "object" ? form.image : null;
            if (img?.files && Object.keys(img.files).length > 0) {
                setFilesState(img.files);
                if (img.language) setLanguageState(img.language);
            } else if (!isEdit) {
                generateFromScratch();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceMode, isEdit]);

    return (
        <Stack spacing={2} sx={{flex: 1, minHeight: 0}}>
            {/* Barre du haut : version + sourceMode + langage */}
            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap"
                }}>
                {currentVersion && (
                    <Chip label={`v${currentVersion}`} size="small" color="primary" variant="outlined"/>
                )}
                <Box sx={{flex: 1}}/>

                {/* Toggle sourceMode */}
                <ToggleButtonGroup
                    value={sourceMode} exclusive size="small"
                    onChange={(_, v) => { if (v) setSourceMode(v); }}
                >
                    <ToggleButton value="BUILD">
                        <Tooltip title={t("analytics.catalog.sourceMode.buildTooltip")} arrow>
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                <BuildIcon sx={{fontSize: 16}}/> {t("analytics.catalog.sourceMode.build")}
                            </Box>
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="PULL">
                        <Tooltip title={t("analytics.catalog.sourceMode.pullTooltip")} arrow>
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                <CloudDownloadIcon sx={{fontSize: 16}}/> {t("analytics.catalog.sourceMode.pull")}
                            </Box>
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="NONE">
                        <Tooltip title={t("analytics.catalog.sourceMode.noneTooltip")} arrow>
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                <BlockIcon sx={{fontSize: 16}}/> {t("analytics.catalog.sourceMode.none")}
                            </Box>
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>

                {sourceMode === "BUILD" && (
                    <TextField select label={t("analytics.catalog.sourceMode.language")} size="small" sx={{width: 120}}
                               value={language} onChange={e => setLanguage(e.target.value)}
                               slotProps={{select: {native: true}}}>
                        {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                    </TextField>
                )}
            </Stack>
            {/* Mode PULL : image existante */}
            {sourceMode === "PULL" && (
                <Paper sx={{p: 3, textAlign: "center", bgcolor: "action.hover", borderRadius: 2}}>
                    <Typography variant="body2" sx={{
                        color: "text.secondary"
                    }}>
                        {t("analytics.catalog.sourceMode.pullDescription")}
                    </Typography>
                </Paper>
            )}
            {/* Mode NONE : pas de build, pas de deploy */}
            {sourceMode === "NONE" && (
                <Paper sx={{p: 3, textAlign: "center", bgcolor: "error.lighter", borderRadius: 2, border: "1px solid", borderColor: "error.light"}}>
                    <Typography variant="body2" sx={{
                        color: "error.main"
                    }}>
                        {t("analytics.catalog.sourceMode.noneDescription")}
                    </Typography>
                </Paper>
            )}
            {/* Mode BUILD : éditeur de code */}
            {sourceMode === "BUILD" && (
                <Stack spacing={1.5} sx={{flex: 1, minHeight: 0}}>
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: "center",
                            gap: 1
                        }}>
                        <Tooltip title={t("analytics.catalog.sourceMode.resetTooltip")} placement="top" arrow>
                            <Button variant="outlined" size="small" color="warning" onClick={() => setResetConfirmOpen(true)} sx={{fontSize: 11}}>
                                {t("analytics.catalog.sourceMode.resetTemplate")}
                            </Button>
                        </Tooltip>
                        <Tooltip title={t("analytics.catalog.sourceMode.exportZipTooltip")} placement="top" arrow>
                            <Button variant="outlined" size="small" onClick={exportZip} sx={{fontSize: 11}}
                                    startIcon={<DownloadIcon sx={{fontSize: 14}}/>}
                                    disabled={Object.keys(files).length === 0}>
                                {t("analytics.catalog.sourceMode.exportZip")}
                            </Button>
                        </Tooltip>
                    </Stack>
                    <Box sx={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column"}}>
                        {isEdit && Object.keys(files).length === 0 ? (
                            <Paper sx={{p: 3, textAlign: "center", bgcolor: "action.hover", borderRadius: 2, flex: 1, display: "flex", alignItems: "center", justifyContent: "center"}}>
                                <Typography variant="body2" sx={{
                                    color: "text.secondary"
                                }}>
                                    {t("analytics.catalog.sourceMode.loadingCode")}
                                </Typography>
                            </Paper>
                        ) : (
                            <CodeEditorIDE files={files} setFiles={setFiles} language={language}
                                           readOnlyFiles={platformFiles}
                                           onRefreshFile={refreshFile}/>
                        )}
                    </Box>
                </Stack>
            )}

            {/* Confirmation de réinitialisation — prévient que le code utilisateur sera écrasé,
                et permet de copier handler / requirements dans le presse-papier avant. */}
            <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <WarningAmberIcon color="warning"/>
                    {t("analytics.catalog.sourceMode.resetConfirmTitle", {defaultValue: "Réinitialiser le template ?"})}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        {t("analytics.catalog.sourceMode.resetConfirmMessage", {defaultValue: "Le code utilisateur (handler, requirements) sera écrasé par le template par défaut. Cette action est irréversible. Copiez votre code avant de continuer si besoin."})}
                    </DialogContentText>
                    {copyableFiles.length > 0 && (
                        <Stack spacing={1}>
                            {copyableFiles.map(name => (
                                <Stack key={name} direction="row" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color={copiedKey === name ? "success" : "primary"}
                                        startIcon={copiedKey === name ? <CheckIcon/> : <ContentCopyIcon/>}
                                        onClick={() => copyFileToClipboard(name)}
                                        sx={{justifyContent: "flex-start", flex: 1}}>
                                        {copiedKey === name
                                            ? t("analytics.catalog.sourceMode.resetCopied", {defaultValue: "Copié : {{name}}", name})
                                            : t("analytics.catalog.sourceMode.resetCopyFile", {defaultValue: "Copier {{name}}", name})}
                                    </Button>
                                    <Tooltip title={t("analytics.catalog.sourceMode.resetDownloadFile", {defaultValue: "Télécharger {{name}}", name})}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="primary"
                                            onClick={() => downloadFile(name)}
                                            sx={{minWidth: 0, px: 1}}>
                                            <DownloadIcon fontSize="small"/>
                                        </Button>
                                    </Tooltip>
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetConfirmOpen(false)}>
                        {t("common.cancel", {defaultValue: "Annuler"})}
                    </Button>
                    <Button onClick={confirmResetTemplate} color="warning" variant="contained">
                        {t("analytics.catalog.sourceMode.resetConfirmAction", {defaultValue: "Réinitialiser"})}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
